import { Roles } from "src/app/models/enum";

const USER_ROLE_CODES = new Set<string>(Object.values(Roles));

/** Assigned in the database only. Hidden from create and edit user forms. */
const DB_ONLY_USER_ROLES = new Set<string>([Roles.GROUP_ADMIN]);

/** Shown only when the API returns this role. It is not created by the app. */
export const HEAD_ADMIN_ROLE = "head admin";

const ROLE_DISPLAY_ORDER = [
	Roles.GROUP_ADMIN,
	HEAD_ADMIN_ROLE,
	Roles.ADMIN,
	Roles.OFFICE_ADMIN,
	Roles.INSTRUCTOR,
	Roles.PILOT,
];

export function isDbOnlyUserRole(role: string): boolean {
	return DB_ONLY_USER_ROLES.has(normalizeUserRoleCode(role));
}

export function sortUserRolesForDisplay<T extends { code?: string | null }>(roles: T[] | null | undefined): T[] {
	return [...(roles ?? [])].sort((left, right) => roleDisplayIndex(left.code) - roleDisplayIndex(right.code));
}

export function assignableUserRoles<T extends { code?: string | null }>(roles: T[] | null | undefined): T[] {
	return sortUserRolesForDisplay((roles ?? []).filter((role) => !isDbOnlyUserRole(String(role.code ?? ""))));
}

/** Keep database-only roles that were already assigned, and drop any newly picked ones. */
export function mergeAssignableUserRoles(selected: unknown, existing: unknown): string[] {
	const assignable = sanitizeUserRoleCodes(selected).filter((role) => !isDbOnlyUserRole(role));
	const preserved = sanitizeUserRoleCodes(existing).filter((role) => isDbOnlyUserRole(role));
	return [...assignable, ...preserved];
}

function normalizeUserRoleCode(role: string): string {
	return role.trim().replace(/\s+/g, " ").toLowerCase();
}

function roleDisplayIndex(code?: string | null): number {
	const index = ROLE_DISPLAY_ORDER.indexOf(normalizeUserRoleCode(String(code ?? "")));
	return index === -1 ? ROLE_DISPLAY_ORDER.length : index;
}

export function sanitizeUserRoleCodes(roles: unknown): string[] {
	if (!Array.isArray(roles)) {
		return [];
	}

	return roles
		.filter((role): role is string => typeof role === "string")
		.map((role) => normalizeUserRoleCode(role))
		.filter((role) => USER_ROLE_CODES.has(role));
}

export function formatUserRoleLabel(role: string): string {
	return role
		.split(" ")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}
